import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Alerts, PageContent, Table, Spinner, WideContainer, FiltersCustom } from 'components';
import { Margin } from 'styled-components-spacing';
import { useFormFields } from 'helpers';
import { useTranslation } from 'react-i18next';

/**
 * What the membership automations have produced, one row per association.
 *
 * The two issuance figures are read against each other: welcome letters well ahead of badges means
 * badge issuing is failing for somebody, which is the whole reason the IPMA office looks at this.
 * The honorary count stands apart from them - an honorary affiliation issues neither.
 *
 * Every association is listed, including those that have issued nothing — a zero is the answer to
 * the question, and leaving the row out would read as the association not existing. The list is
 * short enough to arrive whole, so it is filtered here rather than in another request, and the
 * totals count what is on screen rather than the whole list.
 */
const MembershipStatisticsTable = ({ items, loading, organisationHeading, showHonorary }) => {
  const { t } = useTranslation();
  const [filterFields, handleFilterFieldChange] = useFormFields({
    organisation: '',
    country: null,
  });

  // The countries that actually appear, so the dropdown cannot offer a choice that matches nothing.
  const countryOptions = useMemo(() => {
    const countries = items.map((item) => item.country).filter(Boolean);

    return [...new Set(countries)].sort().map((country) => ({ label: country, value: country }));
  }, [items]);

  const filtersProps = {
    organisation: {
      type: 'input',
      name: 'organisation',
      id: 'f-organisation',
      label: organisationHeading,
      placeholder: t('page.ipma.membershipStatistics.namePlaceholder'),
    },
    country: {
      type: 'select',
      name: 'country',
      id: 'f-country',
      label: t('page.ipma.membershipStatistics.country'),
      placeholder: t('global.selectPlaceholder'),
      options: countryOptions,
    },
  };

  const visibleItems = useMemo(() => {
    const name = (filterFields.organisation || '').trim().toLowerCase();
    const country = filterFields.country ? filterFields.country.value : null;

    return items.filter((item) => {
      if (name && !(item.name || '').toLowerCase().includes(name)) {
        return false;
      }

      return !country || item.country === country;
    });
  }, [items, filterFields]);

  const total = (key) => visibleItems.reduce((sum, item) => sum + (item[key] || 0), 0);
  const columnCount = showHonorary ? 5 : 4;

  return (
    <WideContainer>
      <PageContent>
        <Alerts type="ipma" />
        <p>{t('page.ipma.membershipStatistics.info')}</p>
        <Margin top={{ xs: 24 }}>
          <FiltersCustom
            title={t('page.ipma.membershipStatistics.filterTitle')}
            filters={filtersProps}
            filterFields={filterFields}
            handleFilterFieldChange={handleFilterFieldChange}
            // Nothing to apply: the rows are already here, so the fields are read directly and the
            // table narrows as they are typed rather than on a round trip.
            onFiltersApply={() => {}}
          />
        </Margin>
        <Margin top={{ xs: 24 }}>
          <Table tablebreakpoint="lg">
            <Table.Head>
              <Table.Row>
                <Table.Heading>{organisationHeading}</Table.Heading>
                <Table.Heading>{t('page.ipma.membershipStatistics.country')}</Table.Heading>
                <Table.Heading>{t('page.ipma.membershipStatistics.welcomeLetters')}</Table.Heading>
                <Table.Heading>{t('page.ipma.membershipStatistics.badges')}</Table.Heading>
                {showHonorary ? (
                  <Table.Heading>
                    {t('page.ipma.membershipStatistics.honoraryMembers')}
                  </Table.Heading>
                ) : null}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={columnCount}>
                    <Spinner />
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && !visibleItems.length ? (
                <Table.Row>
                  <Table.Cell colSpan={columnCount}>
                    {t('page.ipma.membershipStatistics.noResults')}
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {!loading &&
                visibleItems.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <Table.Label>{organisationHeading}:</Table.Label>
                      {item.name}
                    </Table.Cell>
                    <Table.Cell>
                      <Table.Label>{t('page.ipma.membershipStatistics.country')}:</Table.Label>
                      {item.country || '-'}
                    </Table.Cell>
                    <Table.Cell>
                      <Table.Label>
                        {t('page.ipma.membershipStatistics.welcomeLetters')}:
                      </Table.Label>
                      {item.welcomeLetters}
                    </Table.Cell>
                    <Table.Cell>
                      <Table.Label>{t('page.ipma.membershipStatistics.badges')}:</Table.Label>
                      {item.badges}
                    </Table.Cell>
                    {showHonorary ? (
                      <Table.Cell>
                        <Table.Label>
                          {t('page.ipma.membershipStatistics.honoraryMembers')}:
                        </Table.Label>
                        {item.honoraryMembers || 0}
                      </Table.Cell>
                    ) : null}
                  </Table.Row>
                ))}
              {!loading && visibleItems.length ? (
                <Table.Row>
                  <Table.Cell>
                    <strong>{t('page.ipma.membershipStatistics.total')}</strong>
                  </Table.Cell>
                  <Table.Cell />
                  <Table.Cell>
                    <Table.Label>{t('page.ipma.membershipStatistics.welcomeLetters')}:</Table.Label>
                    <strong>{total('welcomeLetters')}</strong>
                  </Table.Cell>
                  <Table.Cell>
                    <Table.Label>{t('page.ipma.membershipStatistics.badges')}:</Table.Label>
                    <strong>{total('badges')}</strong>
                  </Table.Cell>
                  {showHonorary ? (
                    <Table.Cell>
                      <Table.Label>
                        {t('page.ipma.membershipStatistics.honoraryMembers')}:
                      </Table.Label>
                      <strong>{total('honoraryMembers')}</strong>
                    </Table.Cell>
                  ) : null}
                </Table.Row>
              ) : null}
            </Table.Body>
          </Table>
        </Margin>
      </PageContent>
    </WideContainer>
  );
};

MembershipStatisticsTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  organisationHeading: PropTypes.string.isRequired,
  // Only Member Associations have Honorary Fellows, so the Young Crew table leaves the column out
  // rather than filling it with zeroes that mean nothing.
  showHonorary: PropTypes.bool,
};

MembershipStatisticsTable.defaultProps = {
  showHonorary: false,
};

export default MembershipStatisticsTable;
